import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const PendingRequestsContext = createContext();

export const usePendingRequests = () => {
  const context = useContext(PendingRequestsContext);
  if (!context) {
    throw new Error('usePendingRequests must be used within a PendingRequestsProvider');
  }
  return context;
};

export const PendingRequestsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingOvertimeCount, setPendingOvertimeCount] = useState(0);
  const [pendingEarlyLeaveCount, setPendingEarlyLeaveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Đếm yêu cầu xin về sớm chờ duyệt (cộng vào badge "Yêu cầu" của admin).
  // Query 1 trường (status) nên không cần composite index.
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      setPendingEarlyLeaveCount(0);
      return undefined;
    }
    const earlyLeaveQuery = query(collection(db, 'early_leave_requests'), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(earlyLeaveQuery, (snapshot) => {
      setPendingEarlyLeaveCount(snapshot.size);
    }, (error) => {
      console.error('Error fetching early leave requests:', error);
      setPendingEarlyLeaveCount(0);
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Đếm đăng ký làm thêm giờ đang chờ duyệt (badge menu "Làm thêm giờ").
  // Query 1 trường (status) nên không cần composite index; manager chỉ đếm bộ phận mình.
  useEffect(() => {
    const canApprove = currentUser && ['admin', 'senior_manager', 'manager'].includes(currentUser.role);
    if (!canApprove) {
      setPendingOvertimeCount(0);
      return undefined;
    }
    const overtimeQuery = query(collection(db, 'overtime_requests'), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(overtimeQuery, (snapshot) => {
      let docs = snapshot.docs.map(d => d.data());
      if ((currentUser.role === 'manager' || currentUser.role === 'senior_manager') && currentUser.department) {
        docs = docs.filter(r => r.department === currentUser.department);
      }
      setPendingOvertimeCount(docs.length);
    }, (error) => {
      console.error('Error fetching overtime requests:', error);
      setPendingOvertimeCount(0);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      setPendingRequestsCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let leaveUnsubscribe = null;
    let lateUnsubscribe = null;
    let editUnsubscribe = null;

    // Fetch yêu cầu xin nghỉ
    const leaveRequestsQuery = query(
      collection(db, 'schedules'),
      where('status', '==', 'on_leave_pending'),
      orderBy('createdAt', 'desc')
    );

    leaveUnsubscribe = onSnapshot(leaveRequestsQuery, (snapshot) => {
      const leaveCount = snapshot.docs.length;
      
      // Fetch yêu cầu đi muộn
      const lateRequestsQuery = query(
        collection(db, 'late_requests'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      lateUnsubscribe = onSnapshot(lateRequestsQuery, (lateSnapshot) => {
        const lateCount = lateSnapshot.docs.length;
        
        // Fetch yêu cầu chỉnh sửa
        const editRequestsQuery = query(
          collection(db, 'edit_requests'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );

        editUnsubscribe = onSnapshot(editRequestsQuery, (editSnapshot) => {
          const editCount = editSnapshot.docs.length;
          
          // Tính tổng số yêu cầu chưa xử lý
          const totalPending = leaveCount + lateCount + editCount;
          setPendingRequestsCount(totalPending);
          setIsLoading(false);
        }, (error) => {
          console.error('Error fetching edit requests:', error);
          setIsLoading(false);
        });
      }, (error) => {
        console.error('Error fetching late requests:', error);
        setIsLoading(false);
      });
    }, (error) => {
      console.error('Error fetching leave requests:', error);
      setIsLoading(false);
    });

    return () => {
      if (leaveUnsubscribe) leaveUnsubscribe();
      if (lateUnsubscribe) lateUnsubscribe();
      if (editUnsubscribe) editUnsubscribe();
    };
  }, [currentUser]);

  const value = {
    pendingRequestsCount: pendingRequestsCount + pendingEarlyLeaveCount,
    pendingEarlyLeaveCount,
    pendingOvertimeCount,
    isLoading,
    setPendingRequestsCount
  };

  return (
    <PendingRequestsContext.Provider value={value}>
      {children}
    </PendingRequestsContext.Provider>
  );
};

